import { Component, EventEmitter, OnInit, Output } from '@angular/core';

import { IField, IProfilePageResponse, IProfilePageSection, IValidationResult, ProfilePageSectionLayout } from '../../shared/interfaces';
import { ApiService } from '../../shared/api.service';
import { IFieldsSection } from './interfaces';
import { InputFieldCreatorService } from '../../shared/input-field-creator.service';
import { ValidatorService } from '../../shared/validator.service';
import { AppDomService } from '../../app-dom.service';
import { TranslationKey } from '../../enums';
import { NotificationMessageType } from '../../shared/enums';
import { NotificationsService } from '../../shared/notifications.service';
import { AppService } from '../../app.service';

@Component({
    selector: 'aza-rws-profile-profile-page',
    templateUrl: 'profile-page.component.html',
    styleUrls: ['profile-page.component.css'],
    standalone: false
})
export class ProfilePageComponent implements OnInit {
    response: IProfilePageResponse;
    fieldsSections: IFieldsSection[];
    @Output() closed = new EventEmitter<void>();

    private errorFormFields: { [key: string]: boolean } = {};
    constructor(
        private apiSvc: ApiService, private inputFieldCreator: InputFieldCreatorService,
        private validatorSvc: ValidatorService, private domSvc: AppDomService,
        private notificationSvc: NotificationsService, private appSvc: AppService
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.response = await this.apiSvc.getProfilePage();
        this.validatorSvc.setContactFields(this.response.fields);
        this.validatorSvc.setContactFieldValidatedCallback(
            (fieldId, value, validationResult, errorsContainerElementId?) =>
                this.contactFieldValidatedCallback(fieldId, value, validationResult, errorsContainerElementId)
        );
        this.fieldsSections = this.createFieldsSections(this.response);
        window.setTimeout(() => {
            this.setInnerHTML(this.fieldsSections);
            const profilePageEl = this.getProfilePageParentElement();
            this.domSvc.fillInputElements(this.response.contact, this.response.fields, profilePageEl);
            this.domSvc.renderRatingFields(profilePageEl);
            this.domSvc.renderOpinionScaleFields(profilePageEl);
        }, 0);
    }

    getProfilePageParentElement(): HTMLElement {
        const profilePageEl = document.querySelector('.profile-profile-page') as HTMLElement;
        return profilePageEl;
    }

    async onConfirm(): Promise<void> {
        if (this.hasFormErrors()) {
            this.notificationSvc.showNotification(
                NotificationMessageType.error, this.appSvc.translate(TranslationKey.oneOrMoreMandatoryFieldsHaveNotBeenCompleted)
            );
            return;
        }
        // TODO: What is the time zone for date and datetime fields ?
        const htmlFormData = this.domSvc.createJsonFromFormFields(
            this.response.fields, [], '', this.getProfilePageParentElement()
        );
        const updatedProfileContact = await this.apiSvc.updateProfileContact(htmlFormData);
        this.notificationSvc.showNotification(
            NotificationMessageType.success, this.appSvc.translate(TranslationKey.profileUpdatedSuccessfully)
        );
    }

    contactFieldValidatedCallback(
        fieldId: string,
        value: any,
        validationResult: IValidationResult,
        errorsContainerElementId?: string
    ): void {
        this.fieldValidated(fieldId, value, validationResult, errorsContainerElementId);
    }

    fieldValidated(fieldId: string, element: HTMLElement, validationResult: IValidationResult, errorsContainerElementId?: string): void {
        if (!validationResult || !errorsContainerElementId) {
            return;
        }
        const inputFieldParentEl = this.domSvc.getInputFieldParent(element);
        if (!inputFieldParentEl) {
            return;
        }
        const errorsEl = this.domSvc.getChildElementByIdPrefix(inputFieldParentEl, errorsContainerElementId);
        if (!errorsEl) {
            return;
        }
        this.manageErrorFormFields(fieldId, validationResult);
        if (validationResult.isValid) {
            // Valid - hide error container element if exists
            this.domSvc.hideElement(errorsEl);
        } else {
            // Invalid - set error container element text to validationResult.errorsText and show it
            if (validationResult.isRequiredButNotProvided) {
                // The field is required but value was not provided
                // errorsEl.innerText = 'This field is required';
            } else {
                // Validation error
                errorsEl.innerText = validationResult.errorsText || '';
            }
            this.domSvc.showElement(errorsEl);
        }
    }

    manageErrorFormFields(fieldId: string, validationResult: IValidationResult): void {
        if (validationResult.isValid) {
            // The field is valid - remove it from the error fields
            delete this.errorFormFields[fieldId];
        } else {
            // The field is invalid - add it to error fields list
            this.errorFormFields[fieldId] = true;
        }
    }

    hasFormErrors(): boolean {
        return Object.keys(this.errorFormFields).length > 0;
    }


    setInnerHTML(fieldsSections: IFieldsSection[]): void {
        for (let i = 0; i < fieldsSections.length; i++) {
            const containerId = 'profile-page-aza-form-field-area-section-index-' + i;
            const selector = `.profile-profile-page #${containerId}`;
            const el = document.querySelector(selector);
            el.innerHTML = fieldsSections[i].fieldsHTML;
        }
    }

    createFieldsSections(profilePageResponse: IProfilePageResponse): IFieldsSection[] {
        const result: IFieldsSection[] = [];
        for (const section of profilePageResponse.page.sections) {
            const fields = this.getSectionFields(profilePageResponse.fields, section);
            const fieldsSection: IFieldsSection = {
                fields: fields, section: section, fieldsHTML: this.getSectionFieldsHtml(section),
                twoColumns: section.layout === ProfilePageSectionLayout.twoColumns
            };
            result.push(fieldsSection);
        }
        return result;
    }

    getSectionFieldsHtml(section: IProfilePageSection): string {
        const sectionFields = this.getSectionFields(this.response.fields, section);
        let result = '';
        for (const field of sectionFields) {
            result += this.inputFieldCreator.createInputFieldBlock(field) + '\r\n';
        }
        return result;
    }

    getSectionFields(allFields: IField[], section: IProfilePageSection): IField[] {
        const result: IField[] = [];
        for (const fieldId of section.field_ids) {
            const field = allFields.find(x => x.id === fieldId);
            if (field) {
                result.push(field);
            }
        }
        return result;
    }

    onClosed(): void {
        this.closed.next();
    }
}
